/**
 * Locations List Page Component
 * /admin/locations
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import apiClient from '../api/client';
import { exportToCSV } from '../utils/export';

const Locations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [locationTypeFilter, setLocationTypeFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadLocations();
  }, [locationTypeFilter, activeFilter]);

  const loadLocations = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (locationTypeFilter) params.location_type = locationTypeFilter;
      if (activeFilter !== '') params.is_active = activeFilter === 'true';
      if (searchTerm) params.search = searchTerm;
      
      const response = await apiClient.getLocations(params);
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setLocations(data);
    } catch (err) {
      setError(err.message || 'Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadLocations();
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete location "${name}"?`)) {
      return;
    }
    try {
      await apiClient.deleteLocation(id);
      setError('');
      loadLocations();
    } catch (err) {
      setError(err.message || 'Failed to delete location');
    }
  };

  const getLocationTypeColor = (type) => {
    const colors = {
      airport: 'primary',
      hotel: 'secondary',
      office: 'info',
      camp: 'warning',
      residential: 'success',
      other: 'default',
    };
    return colors[type] || 'default';
  };

  if (loading && locations.length === 0) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Locations
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={() => {
              const headers = [
                { key: 'name', label: 'Name' },
                { key: 'address', label: 'Address' },
                { key: 'city', label: 'City' },
                { key: 'location_type', label: 'Type' },
                { key: 'latitude', label: 'Latitude' },
                { key: 'longitude', label: 'Longitude' },
                { key: 'is_active', label: 'Active' },
              ];
              exportToCSV(locations, 'locations', headers);
            }}
          >
            Export CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/locations/new')}
          >
            New Location
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3, p: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <form onSubmit={handleSearchSubmit} style={{ flex: 1, minWidth: 200 }}>
            <TextField
              fullWidth
              placeholder="Search locations by name, address..."
              value={searchTerm}
              onChange={handleSearch}
              slotProps={{ input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              } }}
            />
          </form>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Location Type</InputLabel>
            <Select
              value={locationTypeFilter}
              label="Location Type"
              onChange={(e) => setLocationTypeFilter(e.target.value)}
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="airport">Airport</MenuItem>
              <MenuItem value="hotel">Hotel</MenuItem>
              <MenuItem value="office">Office</MenuItem>
              <MenuItem value="camp">Camp</MenuItem>
              <MenuItem value="residential">Residential</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={activeFilter}
              label="Status"
              onChange={(e) => setActiveFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {locations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No locations found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              locations.map((location) => (
                <TableRow key={location.id} hover>
                  <TableCell>{location.name}</TableCell>
                  <TableCell>{location.address || '-'}</TableCell>
                  <TableCell>{location.city || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={location.location_type || 'other'}
                      color={getLocationTypeColor(location.location_type)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={location.is_active ? 'Active' : 'Inactive'}
                      color={location.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/admin/locations/${location.id}`)}
                      title="View"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/admin/locations/${location.id}/edit`)}
                      title="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(location.id, location.name)}
                      title="Delete"
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default Locations;

